import { z } from "zod";
import { Account, AccountKind, AccountUse, ColumnMap, Person, StatementFormat } from "../domain.js";
import { BooleanQuery, Id, IsoMonth, Pence } from "../primitives.js";

export const AccountQuery = z.object({
  includeArchived: BooleanQuery.optional(),
  /** Adds the current month's activity to each account. */
  month: IsoMonth.optional(),
});

export const AccountWithActivity = Account.extend({
  /** null when the month has no rows for this account — not zero. */
  monthInPence: Pence.nullable(),
  monthOutPence: Pence.nullable(),
  txnCount: z.number().int(),
  /**
   * Every row the account holds, over every month and including the transfers
   * the month's figures leave out. What a deletion would destroy.
   */
  allTimeTxnCount: z.number().int(),
  lastTransactionDate: z.string().nullable(),
});

export const AccountList = z.object({
  data: z.array(AccountWithActivity),
  month: IsoMonth.nullable(),
});

/**
 * No `key`. It is derived from the label when the account is created, because
 * nothing that reads a key cares what it says — see `deriveAccountKey`.
 */
export const AccountCreate = z.object({
  label: z.string().trim().min(1).max(60),
  kind: AccountKind,
  /** Omitted means "not said yet", which is a legitimate answer — not a default of "spending". */
  uses: z.array(AccountUse).default([]),
  personId: Id.nullable().optional(),
  statementFormat: StatementFormat.default("generic"),
});

export const AccountPatch = z
  .object({
    label: z.string().trim().min(1).max(60),
    kind: AccountKind,
    /** Replaces the set outright rather than adding to it — there is no "remove one" patch. */
    uses: z.array(AccountUse),
    personId: Id.nullable(),
    statementFormat: StatementFormat,
    columnMap: ColumnMap.nullable(),
    currentBalancePence: Pence.nullable(),
    sortOrder: z.number().int(),
  })
  .partial()
  .refine((patch) => Object.keys(patch).length > 0, "Nothing to change.");

/**
 * What went with the account. Reported rather than assumed, because the count
 * a confirmation promised and the count actually removed are the two numbers
 * that have to agree.
 */
export const AccountDeleteResult = z.object({
  transactionsRemoved: z.number().int(),
  rulesRemoved: z.number().int(),
  statementsRemoved: z.number().int(),
});

export const PersonList = z.object({ data: z.array(Person) });

export const PersonCreate = z.object({
  key: z
    .string()
    .trim()
    .min(1)
    .max(30)
    .regex(/^[a-z0-9_]+$/, "Lower case letters, numbers and underscores only"),
  label: z.string().trim().min(1).max(60),
});

export type AccountQuery = z.infer<typeof AccountQuery>;
export type AccountWithActivity = z.infer<typeof AccountWithActivity>;
export type AccountList = z.infer<typeof AccountList>;
export type AccountCreate = z.infer<typeof AccountCreate>;
export type AccountPatch = z.infer<typeof AccountPatch>;
export type AccountDeleteResult = z.infer<typeof AccountDeleteResult>;
export type PersonList = z.infer<typeof PersonList>;
export type PersonCreate = z.infer<typeof PersonCreate>;
